/**
 * ImpersonationBanner — site-wide top-of-page strip shown whenever an
 * admin is using the "View as" mode. Click "Exit" to drop back into the
 * admin session (audit-logged via `/admin-portal/impersonate/stop`).
 */
import React, { useState } from "react";
import { flushSync } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Eye, X } from "lucide-react";
import { useRole } from "../context/RoleContext";
import { stopImpersonation } from "../lib/adminPortal";

export default function ImpersonationBanner() {
  const {
    isImpersonating, impersonation, originalUser, endImpersonation,
  } = useRole();
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  if (!isImpersonating) return null;

  const targetName = impersonation?.target?.name || "user";
  const targetRole = (impersonation?.targetRole || "")
    .replace(/_/g, " ");

  async function onExit() {
    if (busy) return;
    setBusy(true);
    try {
      // Best-effort: log the stop. We don't block on the response so the
      // UX feels instant even if the API is slow.
      stopImpersonation(
        impersonation.target.id, originalUser?.id,
      ).catch(() => {});
    } finally {
      // Synchronously commit the role swap before navigating so the
      // admin-only landing route doesn't bounce us to /sign-in.
      flushSync(() => endImpersonation());
      navigate("/admin/architects", { replace: true });
      setBusy(false);
    }
  }

  return (
    <div
      data-testid="impersonation-banner"
      role="alert"
      style={{
        position: "sticky",
        top: 0,
        zIndex: 60,
        background: "#1B1F4B",
        color: "#FBFAFD",
        borderBottom: "2px solid #B14E2F",
        padding: "10px 18px",
        display: "flex",
        alignItems: "center",
        gap: 14,
        flexWrap: "wrap",
        fontFamily: '"Nunito Sans", sans-serif',
      }}
    >
      <span
        className="inline-flex items-center"
        style={{
          gap: 6,
          background: "#B14E2F",
          color: "#FBFAFD",
          padding: "3px 9px",
          borderRadius: 999,
          fontSize: 10.5,
          fontWeight: 800,
          letterSpacing: "0.10em",
          textTransform: "uppercase",
        }}
      >
        <Eye size={11} />
        Viewing as
      </span>
      <span
        style={{
          fontSize: 13.5,
          lineHeight: 1.3,
          minWidth: 0,
        }}
      >
        <strong style={{ fontWeight: 700 }}>{targetName}</strong>
        <span style={{ color: "rgba(251,250,253,0.65)", marginLeft: 8 }}>
          · {targetRole}
        </span>
        {originalUser?.name && (
          <span style={{ color: "rgba(251,250,253,0.65)", marginLeft: 8 }}>
            · as admin <em style={{ fontStyle: "italic" }}>{originalUser.name}</em>
          </span>
        )}
      </span>
      <span
        style={{
          fontSize: 11.5,
          color: "rgba(251,250,253,0.55)",
          fontStyle: "italic",
        }}
      >
        All actions are audit-logged.
      </span>
      <button
        type="button"
        onClick={onExit}
        disabled={busy}
        data-testid="impersonation-exit"
        className="inline-flex items-center"
        style={{
          marginLeft: "auto",
          gap: 5,
          background: "transparent",
          border: "1px solid rgba(251,250,253,0.35)",
          borderRadius: 999,
          color: "#FBFAFD",
          fontFamily: '"Nunito Sans", sans-serif',
          fontWeight: 700,
          fontSize: 12,
          letterSpacing: "0.04em",
          padding: "5px 12px",
          cursor: busy ? "not-allowed" : "pointer",
          opacity: busy ? 0.5 : 1,
        }}
      >
        <X size={12} />
        {busy ? "Exiting…" : "Exit impersonation"}
      </button>
    </div>
  );
}
